/**
 * DesignTokensEditor - Admin UI for editing global design tokens
 * 
 * Accessible from Page Builder Index as a "Design" tab/button.
 * Allows admins to edit:
 * - Colors (bg, surface, text, primary, accent, etc.)
 * - Typography (font families, sizes, weights, line heights)
 * - Radius presets
 * - Spacing scale
 * - Shadow presets
 */
import React, { useState, useEffect } from 'react';
import { 
  Palette, Type, Square, Move, Layers, Save, 
  RotateCcw, Check, Loader2, Eye, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useDesignTokens } from './DesignTokensContext';
import { toast } from 'sonner';

// Color preview swatch
const ColorSwatch = ({ name, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  return (
    <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-lg">
      <div 
        className="w-10 h-10 rounded-lg border border-zinc-600 flex-shrink-0 cursor-pointer relative overflow-hidden"
        style={{ backgroundColor: localValue }}
      >
        <input
          type="color"
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            onChange(name, e.target.value);
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium truncate">{name}</p>
        <Input
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            onChange(name, e.target.value);
          }}
          className="h-7 mt-1 bg-zinc-900 border-zinc-700 text-white text-xs font-mono"
        />
      </div>
    </div>
  );
};

// Typography preview
const TypographyPreview = ({ fontFamily, fontSize, fontWeight, lineHeight }) => (
  <div 
    className="p-4 bg-zinc-900 rounded-lg border border-zinc-800"
    style={{ 
      fontFamily, 
      fontSize, 
      fontWeight,
      lineHeight 
    }}
  >
    <p className="text-white">The quick brown fox jumps over the lazy dog.</p>
  </div>
);

const DesignTokensEditor = ({ open, onClose }) => {
  const { tokens, updateTokens, loading, DEFAULT_TOKENS } = useDesignTokens();
  const [localTokens, setLocalTokens] = useState(tokens);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');

  // Sync local tokens with context
  useEffect(() => {
    setLocalTokens(tokens);
    setHasChanges(false);
  }, [tokens, open]);

  // Update local token value
  const updateLocalToken = (category, key, value, subKey = null) => {
    setLocalTokens(prev => {
      const updated = { ...prev };
      if (subKey) {
        updated[category] = {
          ...prev[category],
          [key]: {
            ...prev[category]?.[key],
            [subKey]: value
          }
        };
      } else {
        updated[category] = {
          ...prev[category],
          [key]: value
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateTokens(localTokens);
      if (result.success) {
        toast.success('Design tokens saved successfully');
        setHasChanges(false);
      } else {
        toast.error(result.error || 'Failed to save tokens');
      }
    } catch (err) {
      toast.error('Error saving design tokens');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setLocalTokens(DEFAULT_TOKENS);
    setHasChanges(true);
    toast.info('Reset to default tokens (save to apply)');
  };

  // Discard changes
  const handleDiscard = () => {
    setLocalTokens(tokens);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-white">
              <Palette className="w-5 h-5 text-orange-500" />
              Design Tokens
            </DialogTitle>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <span className="text-xs text-orange-400">Unsaved changes</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDiscard}
                disabled={!hasChanges || saving}
                className="text-zinc-400"
              >
                <X className="w-4 h-4 mr-1" />
                Discard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={saving}
                className="text-zinc-400"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid grid-cols-5 mx-0 bg-zinc-800/50 flex-shrink-0">
            <TabsTrigger value="colors" className="data-[state=active]:bg-orange-500 text-xs">
              <Palette className="w-4 h-4 mr-1" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="typography" className="data-[state=active]:bg-orange-500 text-xs">
              <Type className="w-4 h-4 mr-1" />
              Typography
            </TabsTrigger>
            <TabsTrigger value="radius" className="data-[state=active]:bg-orange-500 text-xs">
              <Square className="w-4 h-4 mr-1" />
              Radius
            </TabsTrigger>
            <TabsTrigger value="spacing" className="data-[state=active]:bg-orange-500 text-xs">
              <Move className="w-4 h-4 mr-1" />
              Spacing
            </TabsTrigger>
            <TabsTrigger value="shadows" className="data-[state=active]:bg-orange-500 text-xs">
              <Layers className="w-4 h-4 mr-1" />
              Shadows
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Colors Tab */}
            <TabsContent value="colors" className="mt-0 space-y-4">
              <p className="text-sm text-zinc-400 mb-4">
                Define your brand color palette. These colors will be available as tokens throughout the Page Builder.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(localTokens.colors || {}).map(([name, value]) => (
                  <ColorSwatch
                    key={name}
                    name={name}
                    value={value}
                    onChange={(n, v) => updateLocalToken('colors', n, v)}
                  />
                ))}
              </div>
            </TabsContent>

            {/* Typography Tab */}
            <TabsContent value="typography" className="mt-0 space-y-6">
              <p className="text-sm text-zinc-400 mb-4">
                Configure typography tokens including font families, sizes, weights, and line heights.
              </p>
              
              {/* Font Families */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Font Families</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(localTokens.typography?.fontFamilies || {}).map(([name, value]) => (
                    <div key={name} className="space-y-1">
                      <Label className="text-zinc-400 text-xs capitalize">{name}</Label>
                      <Input
                        value={value}
                        onChange={(e) => updateLocalToken('typography', 'fontFamilies', e.target.value, name)}
                        className="bg-zinc-800 border-zinc-700 text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Font Sizes */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Font Sizes</Label>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(localTokens.typography?.fontSizes || {}).map(([name, value]) => (
                    <div key={name} className="space-y-1">
                      <Label className="text-zinc-400 text-xs">{name}</Label>
                      <Input
                        value={value}
                        onChange={(e) => updateLocalToken('typography', 'fontSizes', e.target.value, name)}
                        className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Font Weights */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Font Weights</Label>
                <div className="grid grid-cols-6 gap-2">
                  {Object.entries(localTokens.typography?.fontWeights || {}).map(([name, value]) => (
                    <div key={name} className="space-y-1">
                      <Label className="text-zinc-400 text-xs capitalize">{name}</Label>
                      <Input
                        value={value}
                        onChange={(e) => updateLocalToken('typography', 'fontWeights', e.target.value, name)}
                        className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Line Heights */}
              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Line Heights</Label>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(localTokens.typography?.lineHeights || {}).map(([name, value]) => (
                    <div key={name} className="space-y-1">
                      <Label className="text-zinc-400 text-xs capitalize">{name}</Label>
                      <Input
                        value={value}
                        onChange={(e) => updateLocalToken('typography', 'lineHeights', e.target.value, name)}
                        className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-3 pt-4 border-t border-zinc-800">
                <Label className="text-white text-sm font-medium flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Preview
                </Label>
                <TypographyPreview
                  fontFamily={localTokens.typography?.fontFamilies?.body}
                  fontSize={localTokens.typography?.fontSizes?.base}
                  fontWeight={localTokens.typography?.fontWeights?.normal}
                  lineHeight={localTokens.typography?.lineHeights?.normal}
                />
              </div>
            </TabsContent>

            {/* Radius Tab */}
            <TabsContent value="radius" className="mt-0 space-y-4">
              <p className="text-sm text-zinc-400 mb-4">
                Define border radius presets for consistent rounded corners.
              </p>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(localTokens.radius || {}).map(([name, value]) => (
                  <div key={name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-300 text-sm">{name}</Label>
                      <div 
                        className="w-8 h-8 bg-orange-500/20 border-2 border-orange-500"
                        style={{ borderRadius: value }}
                      />
                    </div>
                    <Input
                      value={value}
                      onChange={(e) => updateLocalToken('radius', name, e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Spacing Tab */}
            <TabsContent value="spacing" className="mt-0 space-y-4">
              <p className="text-sm text-zinc-400 mb-4">
                Define spacing scale for margins, paddings, and gaps.
              </p>
              <div className="grid grid-cols-6 gap-3">
                {Object.entries(localTokens.spacing || {}).map(([name, value]) => (
                  <div key={name} className="space-y-1">
                    <Label className="text-zinc-400 text-xs">{name}</Label>
                    <Input
                      value={value}
                      onChange={(e) => updateLocalToken('spacing', name, e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                    />
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Shadows Tab */}
            <TabsContent value="shadows" className="mt-0 space-y-4">
              <p className="text-sm text-zinc-400 mb-4">
                Define shadow presets for elevation and depth.
              </p>
              <div className="space-y-4">
                {Object.entries(localTokens.shadows || {}).map(([name, value]) => (
                  <div key={name} className="flex items-start gap-4 p-3 bg-zinc-800/50 rounded-lg">
                    <div 
                      className="w-16 h-16 bg-zinc-700 rounded-lg flex-shrink-0"
                      style={{ boxShadow: value }}
                    />
                    <div className="flex-1 space-y-1">
                      <Label className="text-white text-sm font-medium capitalize">{name}</Label>
                      <Input
                        value={value}
                        onChange={(e) => updateLocalToken('shadows', name, e.target.value)}
                        className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DesignTokensEditor;
